import { LoggerImpl } from './logger.js';
import {
  ILogger,
  ILoggerFactory,
  InternalLogMessage,
  LogLevel,
  LogMessageMetadata,
  LogRouter,
  LogTransport,
} from './types.js';

export interface LoggerConfig {
  match: RegExp;
  level: LogLevel;
}
export type LoggerConfigTuple = [RegExp, LogLevel];
export type LoggerConfigRegExp = RegExp;

export interface LoggerFactoryOptions {
  transports: Array<LogTransport | null>;
  loggers?: Array<LoggerConfig | LoggerConfigRegExp | LoggerConfigTuple>;
  metadata?: LogMessageMetadata;
  defaultLevel?: LogLevel;
}

class LoggerConfigMatcher {
  constructor(private options: LoggerConfig) {}

  getLoggerLevel(logger: string): LogLevel {
    if (this.options.match.test(logger)) {
      return this.options.level;
    }
    return LogLevel.never;
  }
}

/**
 * Responsible for instantiating Logger's and handling their log messages.
 */
export class LoggerFactory implements LogRouter, ILoggerFactory {
  static Separator = '.';

  transports: Array<LogTransport>;
  configs: Array<LoggerConfigMatcher> | null;
  metadata?: LogMessageMetadata;
  defaultLevel: LogLevel;

  constructor(options: LoggerFactoryOptions) {
    this.metadata = options.metadata;
    this.transports = options.transports.filter((v) => v !== null);
    this.defaultLevel = options.defaultLevel ?? LogLevel.log;
    this.configs =
      options.loggers?.map((filter) => {
        if (filter instanceof RegExp) {
          return new LoggerConfigMatcher({
            match: filter,
            level: LogLevel.debug,
          });
        }
        if (filter instanceof Array) {
          return new LoggerConfigMatcher({
            match: filter[0],
            level: filter[1],
          });
        }
        return new LoggerConfigMatcher(filter);
      }) ?? null;
  }

  setDefaultLevel(level: LogLevel) {
    this.defaultLevel = level;
  }

  private getLoggerLevel(logger: string) {
    if (this.configs == null) return this.defaultLevel;
    return this.configs.reduce((ll, f) => Math.min(f.getLoggerLevel(logger), ll), this.defaultLevel);
  }

  log(loggerLevel: LogLevel, message: InternalLogMessage): void {
    this.transports.forEach((t) => t.log(loggerLevel, message));
  }

  createLogger(path: Array<string> | string, metadata?: LogMessageMetadata): ILogger {
    const id = path instanceof Array ? path.join(LoggerFactory.Separator) : path;
    return new LoggerImpl(id, this, this, this.getLoggerLevel(id), { ...this.metadata, ...metadata });
  }

  async flush(): Promise<void> {
    await Promise.all(this.transports.map((t) => t.flush()));
  }
}
